import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";

import { api } from "../lib/api";
import { Sheet, Err } from "../ui";
import { Mark } from "../brand/Mark";
import { VeehaDisc } from "../brand/Veeha";

/**
 * Veeha — the client assistant, now a React component rather than a
 * Shadow-DOM bolt-on.
 *
 * The widget existed because there was no portal source to put a component
 * into. There is now, and a bolt-on has a real cost: widget/*.js has a stable
 * filename, so the service worker pinned it on first visit and never fetched
 * it again — which is exactly how D'leaps Care shipped and stayed invisible on
 * every installed phone for weeks. Inside the bundle the filename is
 * content-hashed and that class of fault cannot recur.
 *
 * HER NAME LIVES IN TWO PLACES: here and the system prompt in
 * api/routes/portal_chat.php. Change one without the other and she introduces
 * herself by a name the interface does not use.
 *
 * The whole conversation is posted on every turn — the endpoint is stateless
 * and grounds each reply in the signed-in client's own project.
 */

const OPENER =
  "Hello — I'm Veeha. I can see your project, so ask me anything about your schedule, your designs, what's been paid, or your Care cover.";

const CHIPS = [
  "What's happening this week?",
  "What do I owe right now?",
  "When is my handover?",
  "What does my Care cover include?",
];

export function Veeha({ page, seed = "", onClose }) {
  const [log, setLog] = useState([{ role: "assistant", content: OPENER }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const bottom = useRef(null);
  const box = useRef(null);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [log, busy]);

  /* Opened with a question already in hand — from a design idea, say. Asked
     once, on mount: `asked` guards against StrictMode's double-invoke in
     development, which would otherwise send it twice. */
  const asked = useRef(false);
  useEffect(() => {
    if (seed && !asked.current) { asked.current = true; ask(seed); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  async function ask(question) {
    const q = (question ?? text).trim();
    if (!q || busy) return;
    setText("");
    setErr("");
    const next = [...log, { role: "user", content: q }];
    setLog(next);
    setBusy(true);
    try {
      /* The opener is ours, not the model's — sending it back as an assistant
         turn would have her answer questions about a greeting she never
         wrote. Everything after it is real conversation. */
      const messages = next.slice(1).map((m) => ({ role: m.role, content: m.content }));
      const r = await api.post("/portal/chat", { messages, page });
      setLog((l) => [...l, { role: "assistant", content: r.reply || "I could not answer that one." }]);
    } catch (e) {
      setErr(e.message || "Veeha could not be reached.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      className="veeha"
      onClose={onClose}
      title={
        <span className="vh-id">
          <VeehaDisc size={38} />
          <span>
            <b>Veeha</b>
            <small>D&rsquo;leaps AI · here to help with your project</small>
          </span>
        </span>
      }
      footer={
        <div className="vh-ask">
          <textarea
            ref={box}
            value={text}
            placeholder="Ask about your project…"
            aria-label="Ask Veeha"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                ask();
              }
            }}
          />
          <button className="vh-send" onClick={() => ask()} disabled={busy || !text.trim()} aria-label="Send">
            <Send size={17} />
          </button>
        </div>
      }
    >
      <Err onDismiss={() => setErr("")}>{err}</Err>

      <div className="vh-log">
        {log.map((m, i) => (
          <div key={i} className={"vh-msg " + (m.role === "user" ? "me" : "her")}>
            {m.content}
          </div>
        ))}
        {busy && (
          <div className="vh-msg her" aria-live="polite">
            <span className="vh-typing"><i /><i /><i /></span>
          </div>
        )}
        <span ref={bottom} />
      </div>

      {log.length === 1 && (
        <div className="vh-chips">
          {CHIPS.map((c) => (
            <button key={c} onClick={() => ask(c)}>{c}</button>
          ))}
        </div>
      )}

      <p className="muted" style={{ fontSize: ".7rem", marginTop: 16, display: "flex", gap: 6, alignItems: "center" }}>
        <Mark size={14} plain />
        Veeha can see your project only. She has no access to anyone else&rsquo;s.
      </p>
    </Sheet>
  );
}
